import { observer } from 'mobx-react';
import {
  Button,
  createCheckboxField,
  createCurrencyField,
  createSelectField,
  StandardDialog,
} from '@roc/ui';
import { useDrawsStore } from '../hooks';
import { Box, Grid, IconButton, Typography } from '@material-ui/core';
import { darken, makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TableHead
} from '@material-ui/core';
import { Add } from '@material-ui/icons';
import { DrawFieldFormStore } from '../stores/drawFieldFormStore';
import { DrawInformationFormStore } from '../stores/drawInformationFormStore';
import DeleteIcon from '@material-ui/icons/Delete';
import { useEffect } from 'react';
import { ItemDisplay } from '@roc/feature-one-tool';
import { formatCurrency } from '@roc/ui/utils';
import { Document } from '@roc/feature-types';
import { CustomTaskDialog } from '@roc/feature-general-tasks';

const DANGER_COLOR = '#ad4b4b';

const useStyles = makeStyles(() => ({
  noBorders: {
    border: 'none',
  },
  tableContainer: {
    border: "1px solid lightgray",
    borderRadius: "10px",
    padding: "1rem"
  },
  headerCell: {
    color: '#9b9ea3',
    fontWeight: 'bold',
  },
  deleteButton: {
    color: DANGER_COLOR,
    borderColor: DANGER_COLOR,
    '&:hover': {
      color: darken(DANGER_COLOR, 0.1),
      borderColor: darken(DANGER_COLOR, 0.1),
    },
  },
}));

interface DrawInformationDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document;
}

export const DrawInformationDialog = observer(({ open, onClose, document }: DrawInformationDialogProps) => {

  const { drawStore } = useDrawsStore();
  const drawInformationFormStore = drawStore.drawInformationFormStore;
  const taskId = document?.taskId;

  useEffect(() => {
    if (open) {
      drawInformationFormStore.fetchDrawInformation(document?.drawId);
    }
  }, [drawInformationFormStore, open, document]);


  const onSubmit = async () => {

    const result = await drawInformationFormStore.updateDrawInformation(document?.drawId);
    if (result) {
      onClose();
    }
  }

  const dialogContent = (
    <>
      <DrawInformationForm drawInformationFormStore={drawInformationFormStore} drawFieldFormStores={drawInformationFormStore.drawFieldFormStores} />
      <Box display="flex" justifyContent="flex-end" alignItems="center" gridGap="1rem">
        <Button
          testId="cancel"
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          testId="submit"
          variant="outlined"
          color="primary"
          onClick={onSubmit}
        >
          Submit
        </Button>
      </Box>
    </>)

  return (
    <>
      {taskId ? (
        <CustomTaskDialog
          open={open}
          title="Draw Information"
          handleClose={onClose}
          dialogContent={dialogContent}
          taskId={taskId}
        />
      ) : (
        <StandardDialog
          open={open}
          maxWidth="md"
          title="Draw Information"
          handleClose={onClose}
          dialogContent={dialogContent}
        />
      )}
    </>
  )
});

interface DrawInformationFormProps {
  drawInformationFormStore: DrawInformationFormStore;
  drawFieldFormStores: DrawFieldFormStore[];

}

const DrawInformationForm = observer(({ drawInformationFormStore, drawFieldFormStores }: DrawInformationFormProps) => {
  const classes = useStyles();

  return (
    <Box display="flex" flexDirection="column" gridGap="1rem">
      <Grid container>
        <Grid item xs={12} sm={6}>
          {createCurrencyField({
            label: "Draw Amount",
            fieldName: 'drawAmount',
            testId: `drawAmount`,
            store: drawInformationFormStore,
            standaloneLabel: false
          })}
        </Grid>
      </Grid>
      <TableContainer className={classes.tableContainer}>
        <Typography variant="h6" gutterBottom>
          Draw Fields
        </Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                style={{ width: '34%' }}
                className={classes.headerCell}
              >
                Field
              </TableCell>
              <TableCell
                align="center"
                style={{ width: '33%' }}
                className={classes.headerCell}
              >
                Amount
              </TableCell>
              <TableCell
                align="center"
                style={{ width: '20%' }}
                className={classes.headerCell}
              >
                Completed
              </TableCell>
              <TableCell
                align="center"
                style={{ width: '13%' }}
                className={classes.headerCell}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drawFieldFormStores.map(store => (<DrawFieldItem key={store.uuid} drawInformationFormStore={drawInformationFormStore} drawFieldFormStore={store} />))}
            <TableRow>
              <TableCell
                style={{ width: '20%' }}
                className={classes.noBorders}
              >
                <Button
                  size="medium"
                  variant="outlined"
                  color="primary"
                  startIcon={<Add />}
                  testId="add-fee"
                  fullWidth
                  onClick={() => drawInformationFormStore.addNewDrawField()}
                >
                  ADD FIELD
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <ItemDisplay bottomLabel label="Final Wire Amount" value={`${formatCurrency(drawInformationFormStore.finalWireAmount)}`}></ItemDisplay>
    </Box>);
});


interface DrawFieldItemProps {
  drawInformationFormStore: DrawInformationFormStore;
  drawFieldFormStore: DrawFieldFormStore;
}

const DrawFieldItem = observer(({ drawInformationFormStore, drawFieldFormStore }: DrawFieldItemProps) => {

  const uuid = drawFieldFormStore.uuid;

  const classes = useStyles();

  const onDeleteField = () => {
    drawInformationFormStore.deleteDrawField(drawFieldFormStore.uuid);
  }

  return (<TableRow>
    <TableCell
      style={{ width: '34%' }}
    >
      {createSelectField({
        store: drawFieldFormStore,
        testId: `${uuid}-fieldName`,
        fieldName: 'fieldName',
        options: drawInformationFormStore.basicFieldsOptions,
        standaloneLabel: false,
      })}
    </TableCell>
    <TableCell
      align="center"
      style={{ width: '33%' }}
    >
      {createCurrencyField({
        label: "",
        fieldName: 'amount',
        testId: `${uuid}-amount`,
        store: drawFieldFormStore,
        standaloneLabel: false
      })}
    </TableCell>
    <TableCell
      align="center"
      style={{ width: '20%' }}
    >
      {createCheckboxField({
        label: "",
        fieldName: 'completed',
        store: drawFieldFormStore
      })}
    </TableCell>
    <TableCell
      align="center"
      style={{ width: '13%' }}
    >
      <IconButton
        className={classes.deleteButton}
        onClick={() => {
          onDeleteField();
        }}
      >
        <DeleteIcon />
      </IconButton>
    </TableCell>
  </TableRow>);
});